import { useCallback, useMemo } from "react";
import { SegmentedControl as MantineSegmentedControl } from "@mantine/core";
import classnames from "classnames";
import { useField, useFormikContext } from "formik";
import { useColorScheme } from "../../hooks";
import type {
    CustomSegmentedControlCheckboxFactory,
    CustomSegmentedControlCheckboxFormikFactory,
    CustomSegmentedControlFactory,
    CustomSegmentedControlFormikFactory,
    CustomSegmentedControlThemeFactory,
    IProps,
    ISegmentedControlCheckboxFormikProps,
    ISegmentedControlCheckboxProps,
    ISegmentedControlFormikProps,
    ISegmentedControlThemeProps,
} from "./segmented-control.types";
import { componentWrapper } from "../../utilities";
import { isColorScheme, withThemeCrossFade } from "./segmented-control.utilities";
import { ColorScheme, THEME_DATA, THEME_SELECTED_COLORS } from "./segmented-control.constants";
import styles from "./segmented-control.module.scss";

const SegmentedControl = componentWrapper<CustomSegmentedControlFactory, IProps>("SegmentedControl", (props, ref) => {
    return (
        <MantineSegmentedControl
            withItemsBorders={false}
            classNames={{
                root: styles["root"],
                control: styles["control"],
                input: styles["input"],
                label: styles["label"],
                indicator: styles["indicator"],
                innerLabel: styles["innerLabel"],
            }}
            ref={ref}
            {...props}
        />
    );
});

const SegmentedControlFormik = componentWrapper<CustomSegmentedControlFormikFactory, ISegmentedControlFormikProps>(
    "SegmentedControl.Formik",
    ({ name, validate, ...otherProps }, ref) => {
        const [field] = useField({ name, validate });
        return <SegmentedControl ref={ref} {...otherProps} {...field} />;
    }
);

const SegmentedControlCheckbox = componentWrapper<
    CustomSegmentedControlCheckboxFactory,
    ISegmentedControlCheckboxProps
>("SegmentedControlCheckbox", ({ leftLabel, rightLabel, onChange, checked, ...otherProps }, ref) => {
    return (
        <SegmentedControl
            data={[
                { value: "false", label: leftLabel },
                { value: "true", label: rightLabel },
            ]}
            classNames={{
                root: styles["root"],
                control: styles["control"],
                input: styles["input"],
                label: styles["label"],
                indicator: styles["indicator"],
                innerLabel: styles["innerLabel"],
            }}
            value={checked ? "true" : "false"}
            onChange={(value) => {
                if (typeof onChange === "function") {
                    onChange(value === "true");
                }
            }}
            ref={ref}
            {...otherProps}
        />
    );
});

const SegmentedControlCheckboxFormik = componentWrapper<
    CustomSegmentedControlCheckboxFormikFactory,
    ISegmentedControlCheckboxFormikProps
>("SegmentedControlCheckbox.Formik", ({ name, validate, onChange, ...otherProps }, ref) => {
    const [{ onChange: fieldOnChange, value, ...field }] = useField({ name, type: "checkbox", validate });
    const { setFieldValue } = useFormikContext();
    return (
        <SegmentedControlCheckbox
            ref={ref}
            {...otherProps}
            onChange={(value) => {
                setFieldValue(name, value);
                onChange?.(value);
            }}
            {...field}
        />
    );
});

/**
 * Icon-only segmented control that shows and sets the colour scheme: light, dark, or the operating system's preference.
 * It has its own look, used only for the theme picker: each option's selected segment has its own colour.
 * @param props.onChange Called when the user picks a different colour scheme, with the new and the previous one.
 * @returns {JSX.Element} The SegmentedControlTheme component.
 */
const SegmentedControlTheme = componentWrapper<CustomSegmentedControlThemeFactory, ISegmentedControlThemeProps>(
    "SegmentedControlTheme",
    ({ onChange: onColorSchemeChange, ...props }, ref) => {
        const { colorScheme, setColorScheme } = useColorScheme();

        const classNames = useMemo(
            () => ({
                root: classnames(styles["root"], styles["theme-root"]),
                indicator: classnames(styles["indicator"], {
                    [styles["theme-indicator-system"]]: colorScheme === ColorScheme.System,
                }),
                label: classnames(styles["label"], styles["theme-label"]),
            }),
            [colorScheme]
        );

        // The option itself sets each segment's size, so the label adds no padding. The selected colours go through
        // vars, not the color prop, because the theme's --sc-color would win over the prop. Without the prop Mantine
        // also adds a shadow to the selected segment, which the design doesn't have.
        const vars = useCallback(
            () => ({
                root: {
                    "--sc-padding": "0",
                    "--sc-shadow": "none",
                    "--sc-color": THEME_SELECTED_COLORS[colorScheme].background,
                    "--sc-label-color": THEME_SELECTED_COLORS[colorScheme].icon,
                },
            }),
            [colorScheme]
        );

        const onChange = useCallback(
            (value: string) => {
                if (isColorScheme(value)) {
                    withThemeCrossFade(() => setColorScheme(value));
                    if (isColorScheme(colorScheme)) {
                        onColorSchemeChange?.(value, colorScheme);
                    }
                }
            },
            [colorScheme, setColorScheme, onColorSchemeChange]
        );

        return (
            <SegmentedControl
                ref={ref}
                aria-label="Theme"
                {...props}
                classNames={classNames}
                bd="1px solid var(--border-default-color)"
                bg="var(--surface-gray-transparent-color)"
                vars={vars}
                data={THEME_DATA}
                value={colorScheme}
                onChange={onChange}
            />
        );
    }
);

SegmentedControlCheckbox.Formik = SegmentedControlCheckboxFormik;

SegmentedControl.Checkbox = SegmentedControlCheckbox;
SegmentedControl.Formik = SegmentedControlFormik;
SegmentedControl.Theme = SegmentedControlTheme;

export {
    SegmentedControl,
    SegmentedControlFormik,
    SegmentedControlCheckbox,
    SegmentedControlCheckboxFormik,
    SegmentedControlTheme,
};
